import { useState, useRef, useEffect, type FC } from 'react';
import Icon, { type IconString } from '2-core-elements/Icon/Icon';

interface SelectCustomOption {
    label: string;
    value: string;
}

interface SelectCustomProps {
    icon?: IconString;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    id?: string;
    size?: 'sm' | 'md' | 'lg';
    options: SelectCustomOption[];
    onChange?: (newValue: SelectCustomOption | null) => void;
    value?: SelectCustomOption | null;
    freezePlaceholder?: boolean;
}

interface SelectCustomOptionProps {
    label: string;
    onClick: () => void;
    isActive?: boolean;
}

type ObjectCheck = SelectCustomOption | null | undefined;

function isValueEqual(a: ObjectCheck, b: ObjectCheck): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

const SelectCustomOption: FC<SelectCustomOptionProps> = ({ label, onClick, isActive }) => {
    const optionClass = `select-custom-option ${isActive ? 'is-active' : ''}`;

    return (
        <button type="button" className={optionClass} onClick={onClick}>
            {label}
        </button>
    );
};

const SelectCustom: FC<SelectCustomProps> = props => {
    const {
        icon = 'chevron-down',
        className = '',
        disabled = false,
        placeholder = '',
        id,
        size = 'md',
        options,
        onChange,
        value,
        freezePlaceholder,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [values, setValues] = useState<SelectCustomOption | null | undefined>(undefined);
    const element = useRef<HTMLDivElement>(null);

    const onClick = (): void => {
        setIsOpen(!isOpen);
    };

    const handleOutsideClick = (e: Event): void => {
        if (element?.current?.contains(e.target as Node | null)) {
            // handle click inside
            return;
        }
        // handle click outside
        setIsOpen(false);
    };

    // add & remove listener on didMount and unMount
    useEffect(() => {
        if (value) {
            setValues(value);
        }

        window.addEventListener('mousedown', handleOutsideClick);

        // returned function will be called on component unmount
        return (): void => {
            window.removeEventListener('mousedown', handleOutsideClick);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // if not the same value, then set the value
        if (!isValueEqual(value, values)) {
            setValues(value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (onChange && values !== undefined && !isValueEqual(value, values)) {
            onChange(values);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const classOutput = `select-custom-wrapper ${className} ${isOpen ? 'is-open' : ''}`;
    const activeClass = values ? 'is-active' : '';
    const selectClassOutput = `select-wrapper ${activeClass} ${icon ? 'has-suffix-icon' : ''} select-${size} ${className}`;

    return (
        <div ref={element} className={classOutput}>
            <div className={selectClassOutput}>
                {icon && <Icon className="suffix-icon" icon={icon} />}
                <button
                    type="button"
                    onClick={onClick}
                    className="select"
                    disabled={disabled}
                    id={id}
                    aria-controls={id}
                    role="combobox"
                    aria-expanded={isOpen}
                >
                    {(freezePlaceholder || (placeholder && !values)) && (
                        <span className="select-placeholder">{placeholder}</span>
                    )}
                    {!freezePlaceholder && values && (
                        <span className="select-text">{values.label}</span>
                    )}
                </button>
            </div>
            {!!options?.length && (
                <div className="select-custom-content">
                    <div className="select-custom-content-container">
                        {options.map(({ label, value: optionValue }, i) => {
                            const key = `${label}-${value}-${i}`;

                            return (
                                <SelectCustomOption
                                    key={key}
                                    label={label}
                                    onClick={(): void => {
                                        if (values && values.value === optionValue) {
                                            setValues(null);
                                        } else {
                                            setValues({ label, value: optionValue });
                                        }
                                        setIsOpen(false);
                                    }}
                                    isActive={!!values && values.value === optionValue}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectCustom;
